<script setup lang="ts">
import ErrorBoundary from "@/components/ErrorBoundary/index.vue";
import OpenningPage from "@/components/OpenningPage/index.vue";
import getRoutes, { genRoutes } from "@/router/initRoutes";
import {
  useSystemInfo,
  useActivityList,
  useGameCategory,
  useHomeBannersAndTexts,
  useOpenningImage,
  useWallet,
  useServico,
  // useAppPwa,
} from "@/store";
import useAPP from "@/hooks/useInstall";
import { ev, CUSTOM_PROTOCOL, INSTALLCONFIG_TIME } from "@/consts";
import {
  sealing,
  isTemplate,
  isPlatform,
  isBuild,
  isIOSSafari,
  isInPWA,
  hasNetwork,
} from "@/env";
import { iframeUrl, checkPWAAndPreventionJump } from "@/hooks/usePWAJumpIframe";
import { callJSBridge } from "@/utils";
import createHeadLinks from "@/utils/createHeadLinks";
import { needUpdate } from "@/utils/updateChecker";
import { checker } from "@/utils/updateChecker";
import showPopup from "@/components/showPopup";
import Reload from "@/components/common/Reload.vue";
const notificationModal = defineAsyncComponent(() => import("@/components/NotificationModal/index.vue"));
import { useUserInfo } from "@/store";
import FullScreenLoading from "@/components/FullScreenLoading/index.vue";
import GameFlowPopup from "@/components/GameFlowPopup/index.vue";
import { isInIframe } from "@/env";
// import i18n from '@/i18n'
import NotFound from "@/views/__404/index.vue";
import { useGameFlowStore } from "@/store";
const flowStore = useGameFlowStore();

const isLoading = ref(true);
const router = useRouter();
const route = useRoute();
// const userInfo = useUserInfo();
if (isBuild && !localStorage._checker) {
  checker.start();
}

const genCommonRoutes = async () => {
  const themeModule = await import("@/router/theme_common").then((m) => m.default);
  const theme = useSystemInfo().theme;
  const routes = await genRoutes(themeModule, theme);
  routes.forEach(router.addRoute);
};

const init = async () => {
  if (route.query.s === "1") return;

  useHomeBannersAndTexts().init()
  checkPWAAndPreventionJump()


  if (iframeUrl.value) {
    //外层，不需要加载
    return;
  }

  showLoadingToast({
    message: "loading...",
    forbidClick: true,
    duration: 0,
  });

  await genCommonRoutes();

  const initData = () => {
    localStorage.removeItem(INSTALLCONFIG_TIME);
    const t = localStorage.getItem(INSTALLCONFIG_TIME + "_tid");
    if (t) {
      // localStorage.removeItem(INSTALLCONFIG_TIME + '_tid');
      clearTimeout(+t);
    }
    useOpenningImage().init()
    useActivityList().init()
    useGameCategory().init()
    useServico().init()
    useWallet().init()

  };

  if (!sealing) {
    ev.on("login", () => {
      initData();
    });

    ev.on("logout", () => {
      initData();
    });
  }

  const initRoutes = async () => {
    const { theme, skin } = useSystemInfo();
    // const routes = await getRoutes(theme, 'purple_background_red');
    const routes = await getRoutes(theme, skin);
    [
      ...routes,
      {
        path: "/:pathMatch(.*)*",
        name: "404",
        component: NotFound,
      },
    ].forEach(router.addRoute);
  };

  await initRoutes()
  initData()
};

const getPwaRedirect = () => {
  return (
    "/" +
    decodeURIComponent(route.query.pwaredirect!.toString()).replace(
      CUSTOM_PROTOCOL + "://",
      ""
    )
  ).replace(/^\/+/, "/");
};

const showPage = async () => {
  // router.removeRoute("openning");
  const isPwaDirect = !!route.query.pwaredirect;
  const url = isPwaDirect ? getPwaRedirect() : location.pathname + location.search;
  await router.replace(url);

  createHeadLinks(useHomeBannersAndTexts().pwaConfig);

  isLoading.value = false;
  closeToast();

  useAPP().init()
  useSystemInfo().setIsInit(true)
};

init().then(showPage);

const cachedRoutes = ref<string[]>([]);
watch(route, (newRoute) => {
  if (newRoute.meta.keepAlive) {
    if (!cachedRoutes.value.includes(newRoute.name as string)) {
      cachedRoutes.value.push(newRoute.name as string);
    }
  }

  if (needUpdate.value && ["home", "promocao", "meu"].includes(newRoute.name as string)) {
    if (needUpdate.value === "popup") {
      showPopup(Reload);
    } else {
      location.reload();
    }
  }
});

watch(
  isLoading,
  (newVal) => {
    if (isPlatform && !newVal) {
      // 在安卓包中且不是马甲包，关闭启动页
      callJSBridge("closeSplashScreen", "");
    }
    // 如果是在iframe中，关闭PWA遮罩loding
    if (isInIframe && newVal) {
      setTimeout(() => {
        window.parent.postMessage("iframe-content-ready", "*");
      }, 800);
    }
  },
  {
    immediate: true,
  }
);
watch(
  () => useUserInfo().isLoggedin,
  (newVal) => {
    if (newVal && isIOSSafari && !isInPWA) {
      window.location.reload();
    }
  }
);

// 检测断网
window.addEventListener("offline", () => {
  handleOffline();
});
window.addEventListener("online", () => {
  handleOnline();
});

const handleOffline = () => {
  hasNetwork.value = false;
  console.log("当前网络已断开");
};

const handleOnline = () => {
  hasNetwork.value = true;
  setTimeout(() => {
    location.reload();
  }, 1000);
  console.log("当前网络已连接");
};

// if ((window as any).jsBridge) {
//   (window as any).jsBridge.postMessage(
//     'setUserInfo',
//     JSON.stringify(userInfo || {})
//   );
// }
const isLoadingPwa = ref(true);
// const sendAuthToIframe = () => {
//   const iframe: any = document.getElementById("gameIframe");
//   if (iframe && iframe.contentWindow) {
//     isLoadingPwa.value = false;
//   }
// };

const handleIframeMessage = (event: MessageEvent) => {
  if (event.data === "iframe-content-ready") {
    isLoadingPwa.value = false;
  }
};

onMounted(() => {
  window.addEventListener("message", handleIframeMessage);
  // 在 App.vue 或特定页面添加
  document.addEventListener(
    "dblclick",
    (e) => {
      e.preventDefault();
    },
    { passive: false }
  );
});

onUnmounted(() => {
  window.removeEventListener("message", handleIframeMessage);
  window.removeEventListener("offline", handleOffline);
  window.removeEventListener("online", handleOnline);
});
</script>

<template>
  <notificationModal v-if="!isLoading" />
  <template v-if="iframeUrl">
    <FullScreenLoading v-if="isLoadingPwa" />
    <iframe id="gameIframe" style="width: 100%; height: calc(var(--vh, 1vh) * 100); border: none" :src="iframeUrl"
      sandbox="allow-same-origin allow-scripts allow-popups allow-forms" allow="screen-wake-lock" />
  </template>
  <template v-else>
    <OpenningPage v-if="isLoading && !isTemplate" />
    <router-view v-slot="{ Component }">
      <ErrorBoundary>
        <keep-alive :include="cachedRoutes">
          <component :is="Component" />
        </keep-alive>
      </ErrorBoundary>
    </router-view>
    <van-popup v-model:show="flowStore.isVisible" position="bottom" :style="{ height: '90%' }" :close-on-popstate="true"
      :close-on-click-overlay="false" @close="flowStore.hide()" :safe-area-inset-bottom="true">
      <GameFlowPopup v-if="flowStore.isVisible" :initial-data="flowStore.flowProps" :type="flowStore.flowType"
        @finish="flowStore.finish" @close="flowStore.hide" />
    </van-popup>
  </template>
</template>
